import { useEffect, useMemo, useState } from 'react';
import {
  ApolloClient,
  gql,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { ConfigHelper } from '@devxdiscover/devhub-common';
import { AiUseCase, STUBBED_USE_CASES, USE_STUBBED_USE_CASES } from './types';

const APPROVED_INTAKE_STATUSES = [
  'APPROVED',
  'APPROVED_WITH_CONDITIONS',
  'AUTO_APPROVED',
];

/**
 * Returns true if a use case can be actioned (linked / used to create an AI System).
 * @param uc - The AI use case to check
 */
export const isActionableUseCase = (uc: AiUseCase) =>
  !!uc.overallIntakeStatus &&
  APPROVED_INTAKE_STATUSES.includes(uc.overallIntakeStatus);

/**
 * Fetches all of the current user's AI use cases from the Galileo supergraph
 * via the User.aiUseCases federated field (Snow subgraph). All statuses are
 * returned so users can see pending cases — action buttons are disabled for
 * unapproved cases in the UI.
 *
 * While USE_STUBBED_USE_CASES is true (types.ts), the Galileo query is
 * bypassed entirely and STUBBED_USE_CASES is returned directly. Flip the
 * flag to false once Snow's subgraph is live in fusionrage.
 */

const UserAiUseCasesQuery = gql`
  query UserAiUseCases($login: String!) {
    identity {
      user(login: $login) {
        aiUseCases {
          useCaseId
          useCaseSysId
          title
          businessUseCase
          status
          overallIntakeStatus
          requestedBy {
            login
            displayName
          }
        }
      }
    }
  }
`;

export interface UseApprovedUseCasesResult {
  useCases: AiUseCase[];
  loading: boolean;
  error: Error | undefined;
  reload: () => void;
}

/**
 * Fetches all AI use cases for the current user from the Galileo supergraph.
 */
export function useApprovedUseCases(): UseApprovedUseCasesResult {
  const identityApi = useApi(identityApiRef);
  const [useCases, setUseCases] = useState<AiUseCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // aiUseCases is added by Snow's subgraph via extend type User — it only exists
  // in the Galileo supergraph, not in Fusion. Always route through /api/galileo/query.
  const galileoClient = useMemo(
    () =>
      new ApolloClient({
        link: createHttpLink({
          uri: ConfigHelper.instance.apiUrl('galileo/query').href,
          credentials: 'include',
        }),
        cache: new InMemoryCache(),
      }),
    [],
  );

  const load = () => {
    if (USE_STUBBED_USE_CASES) {
      setUseCases(STUBBED_USE_CASES);
      return;
    }
    setLoading(true);
    setError(undefined);
    identityApi
      .getBackstageIdentity()
      .then(async identity => {
        const login = identity.userEntityRef.split('/').pop() ?? '';
        if (!login) return;
        try {
          const result = await galileoClient.query<{
            identity: { user: { aiUseCases: AiUseCase[] } };
          }>({
            query: UserAiUseCasesQuery,
            variables: { login },
            fetchPolicy: 'network-only',
          });
          setUseCases(result.data?.identity?.user?.aiUseCases ?? []);
        } catch (e) {
          setError(e instanceof Error ? e : new Error(String(e)));
        } finally {
          setLoading(false);
        }
      })
      .catch(e => {
        setError(e instanceof Error ? e : new Error(String(e)));
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { useCases, loading, error, reload: load };
}
